import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  CalibrationParticipantType,
  CalibrationSessionQuery,
  CalibrationSessionState,
} from '../../../__generated__/graphql';

export type CalibrationOption = {
  label: string;
  value: number;
};

type PendingChange = {
  employeeTopicId: string;
  rate: number;
  expectedRate: number | null;
  notes?: string;
};

type CalibrationWorkspaceContextValue = {
  session: CalibrationSessionQuery['calibrationSession'];
  pendingChanges: Map<string, PendingChange>;
  conflicts: Set<string>;
  calibrationOptions: CalibrationOption[];
  userRole: CalibrationParticipantType | null;
  sessionState: string;
  isEditable: boolean;
  setPendingChange: (
    employeeTopicId: string,
    rate: number,
    expectedRate: number | null,
    notes?: string,
  ) => void;
  removePendingChange: (employeeTopicId: string) => void;
  clearPendingChanges: () => void;
  addConflict: (employeeTopicId: string) => void;
  clearConflicts: () => void;
  selectedEmployeeTopicId: string | null;
  openHistory: (employeeTopicId: string) => void;
  closeHistory: () => void;
  refetchSession: () => Promise<unknown>;
  activeNoteCellId: string | null;
  openNotePopover: (employeeTopicId: string) => void;
  closeNotePopover: () => void;
};

const CalibrationWorkspaceContext =
  createContext<CalibrationWorkspaceContextValue | null>(null);

export const useCalibrationWorkspace = (): CalibrationWorkspaceContextValue => {
  const context = useContext(CalibrationWorkspaceContext);
  if (!context) {
    throw new Error(
      'useCalibrationWorkspace must be used within CalibrationWorkspaceProvider',
    );
  }
  return context;
};

type Props = {
  session: CalibrationSessionQuery['calibrationSession'];
  refetchSession: () => Promise<unknown>;
  children: React.ReactNode;
};

export const CalibrationWorkspaceProvider: React.FC<Props> = ({
  session,
  refetchSession,
  children,
}) => {
  const [pendingChanges, setPendingChanges] = useState<
    Map<string, PendingChange>
  >(new Map());
  const [conflicts, setConflicts] = useState<Set<string>>(new Set());
  const [selectedEmployeeTopicId, setSelectedEmployeeTopicId] = useState<
    string | null
  >(null);
  const [activeNoteCellId, setActiveNoteCellId] = useState<string | null>(null);

  const calibrationOptions = useMemo<CalibrationOption[]>(
    () =>
      session.options.map((opt) => ({
        label: opt.label,
        value: opt.rate,
      })),
    [session.options],
  );

  const userRole = session.currentEmployeeParticipantType ?? null;
  const sessionState = session.flair__State__c;

  const isEditable =
    userRole === CalibrationParticipantType.Calibrator &&
    sessionState === CalibrationSessionState.InProgress;

  const setPendingChange = useCallback(
    (
      employeeTopicId: string,
      rate: number,
      expectedRate: number | null,
      notes?: string,
    ) => {
      setPendingChanges((prev) => {
        const next = new Map(prev);
        next.set(employeeTopicId, {
          employeeTopicId,
          rate,
          expectedRate,
          notes,
        });
        return next;
      });
      setConflicts((prev) => {
        if (!prev.has(employeeTopicId)) return prev;
        const next = new Set(prev);
        next.delete(employeeTopicId);
        return next;
      });
    },
    [],
  );

  const removePendingChange = useCallback((employeeTopicId: string) => {
    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.delete(employeeTopicId);
      return next;
    });
  }, []);

  const clearPendingChanges = useCallback(() => {
    setPendingChanges(new Map());
  }, []);

  const addConflict = useCallback((employeeTopicId: string) => {
    setConflicts((prev) => {
      const next = new Set(prev);
      next.add(employeeTopicId);
      return next;
    });
  }, []);

  const clearConflicts = useCallback(() => {
    setConflicts(new Set());
  }, []);

  const openHistory = useCallback((employeeTopicId: string) => {
    setSelectedEmployeeTopicId(employeeTopicId);
  }, []);

  const closeHistory = useCallback(() => {
    setSelectedEmployeeTopicId(null);
  }, []);

  const openNotePopover = useCallback((employeeTopicId: string) => {
    setActiveNoteCellId(employeeTopicId);
  }, []);

  const closeNotePopover = useCallback(() => {
    setActiveNoteCellId(null);
  }, []);

  const value = useMemo<CalibrationWorkspaceContextValue>(
    () => ({
      session,
      pendingChanges,
      conflicts,
      calibrationOptions,
      userRole,
      sessionState,
      isEditable,
      setPendingChange,
      removePendingChange,
      clearPendingChanges,
      addConflict,
      clearConflicts,
      refetchSession,
      selectedEmployeeTopicId,
      openHistory,
      closeHistory,
      activeNoteCellId,
      openNotePopover,
      closeNotePopover,
    }),
    [
      session,
      pendingChanges,
      conflicts,
      calibrationOptions,
      userRole,
      sessionState,
      isEditable,
      setPendingChange,
      removePendingChange,
      clearPendingChanges,
      addConflict,
      clearConflicts,
      refetchSession,
      selectedEmployeeTopicId,
      openHistory,
      closeHistory,
      activeNoteCellId,
      openNotePopover,
      closeNotePopover,
    ],
  );

  return (
    <CalibrationWorkspaceContext.Provider value={value}>
      {children}
    </CalibrationWorkspaceContext.Provider>
  );
};
